import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OfflineStorageService } from './offline-storage.service';
import { environment } from '../../environments/environment';

/** Scope used when an authorizer ID has to be resolved from the server during sync. */
export interface AuthorizerResolveContext {
  passportId?: string | number;
  roleId?: number;
}

/** Shape of the authorizer records cached in IndexedDB (`authorisedUsers` store). */
interface CachedAuthorizerRecord {
  authorizerId?: number;
  id?: number;
  authorizerName?: string;
  name?: string;
  userId?: number;
  passportId?: number;
  roleId?: number;
  isPendingSync?: boolean;
  addedOffline?: boolean;
  timestamp?: number;
}

interface AuthorizerRequestBody {
  authorizerName: string;
  userId?: number;
  passportId?: number;
  roleId?: number;
}

/** RoleID used by the backend for Grower account-scoped authorizers. */
const GROWER_ROLE_ID = 2;

@Injectable({
  providedIn: 'root'
})
export class AuthorizerSyncService {
  private readonly http = inject(HttpClient);
  private readonly offlineStorage = inject(OfflineStorageService);
  private authorisedByMap: Map<string, number> | null = null;

  /**
   * Set the authorisedByMap reference (called from passport-details page)
   */
  setAuthorisedByMap(map: Map<string, number>): void {
    this.authorisedByMap = map;
  }

  /**
   * Get authorizer ID from map by name
   * Returns undefined if not found
   */
  getAuthorizerId(name: string): number | undefined {
    if (!this.authorisedByMap) {
      return undefined;
    }
    const id = this.authorisedByMap.get(name);
    // Return undefined if ID is negative (pending sync)
    return id && id > 0 ? id : undefined;
  }

  /**
   * Resolves an authorizer ID for a name that reached sync without one.
   * Order: cached authorizers in IndexedDB → authorizers add API (idempotent on name + scope,
   * so an authorizer that already exists in the database returns its existing ID).
   * Callers check the in-memory map first via getAuthorizerId(); that map is only populated
   * while the passport-details page is open, so it is empty for a Resync from the list page.
   */
  async resolveAuthorizerIdFromCacheOrApi(
    name: string,
    context: AuthorizerResolveContext = {}
  ): Promise<number | undefined> {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      return undefined;
    }

    const cachedId = await this.findAuthorizerIdInCache(trimmed);
    if (cachedId) {
      console.log(`📦 Resolved authorizer "${trimmed}" from offline cache → ID: ${cachedId}`);
      this.rememberAuthorizerId(trimmed, cachedId);
      return cachedId;
    }

    const registeredId = await this.registerAuthorizerAndGetId(trimmed, context);
    if (!registeredId) {
      return undefined;
    }

    console.log(`🆔 Resolved authorizer "${trimmed}" via authorizers API → ID: ${registeredId}`);
    this.rememberAuthorizerId(trimmed, registeredId);
    await this.mergeAuthorizerIntoCache(trimmed, registeredId);
    return registeredId;
  }

  /**
   * Looks the name up in the cached authorizer list (written by the list pages and by
   * passport-details). Matching is trimmed and case-insensitive because the name on a queued
   * submission can come from a different API field than the authorizer list.
   */
  private async findAuthorizerIdInCache(name: string): Promise<number | undefined> {
    const userIdNum = this.getCurrentUserIdNumber();
    if (userIdNum === undefined) {
      return undefined;
    }

    try {
      const cached: CachedAuthorizerRecord[] = (await this.offlineStorage.getAuthorisedUsers(userIdNum)) ?? [];
      const match = cached.find(entry => AuthorizerSyncService.matchesName(entry, name.toLowerCase()));
      const id = match?.authorizerId ?? match?.id;
      return id !== undefined && id > 0 ? id : undefined;
    } catch (error) {
      console.warn(`⚠️ Could not read cached authorizers while resolving "${name}":`, error);
      return undefined;
    }
  }

  /**
   * Adds the authorizer through the API to obtain its ID. sp_AddAuthorizer is idempotent on
   * (Name, scope), so an existing authorizer is returned instead of being duplicated.
   */
  private async registerAuthorizerAndGetId(
    name: string,
    context: AuthorizerResolveContext
  ): Promise<number | undefined> {
    if (!navigator.onLine) {
      console.warn(`📴 Device is offline - cannot resolve authorizer "${name}" from the server`);
      return undefined;
    }

    const headers = this.buildAuthHeaders();
    const userIdNum = this.getCurrentUserIdNumber();
    if (!headers || userIdNum === undefined) {
      return undefined;
    }

    const body = AuthorizerSyncService.buildResolveRequestBody(name, userIdNum, context);
    const apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers/add`;

    try {
      console.log(`📤 Resolving authorizer "${name}" via add API:`, body);
      const response = await firstValueFrom(
        this.http.post<unknown>(apiUrl, body, { headers, withCredentials: true })
      );
      return AuthorizerSyncService.extractAuthorizerId(response);
    } catch (error) {
      console.warn(`❌ Failed to resolve authorizer "${name}" via add API:`, error);
      return undefined;
    }
  }

  /**
   * Stores a resolved ID in the shared in-memory map so later lookups in the same session
   * (including Resync from the list page) hit without another cache or API round trip.
   */
  private rememberAuthorizerId(name: string, id: number): void {
    this.authorisedByMap ??= new Map<string, number>();
    this.authorisedByMap.set(name, id);
  }

  /**
   * Writes a resolved ID back into the cached authorizer list so it survives a page reload.
   */
  private async mergeAuthorizerIntoCache(name: string, id: number): Promise<void> {
    const userIdNum = this.getCurrentUserIdNumber();
    if (userIdNum === undefined) {
      return;
    }

    try {
      const cached: CachedAuthorizerRecord[] = (await this.offlineStorage.getAuthorisedUsers(userIdNum)) ?? [];
      const index = cached.findIndex(entry => AuthorizerSyncService.matchesName(entry, name.toLowerCase()));

      if (index >= 0) {
        cached[index] = { ...cached[index], authorizerId: id, isPendingSync: false, addedOffline: false };
      } else {
        cached.push({
          authorizerId: id,
          authorizerName: name,
          userId: userIdNum,
          isPendingSync: false,
          addedOffline: false,
          timestamp: Date.now()
        });
      }

      await this.offlineStorage.saveAuthorisedUsers(userIdNum, cached);
    } catch (error) {
      console.warn(`⚠️ Could not cache resolved authorizer "${name}":`, error);
    }
  }

  private static matchesName(entry: CachedAuthorizerRecord, lowerCaseName: string): boolean {
    const entryName = entry.authorizerName ?? entry.name ?? '';
    return entryName.trim().toLowerCase() === lowerCaseName;
  }

  /**
   * Grower submissions are Grower-account scoped (roleId 2); Transloader submissions are
   * station scoped (roleId omitted). Without a passport, fall back to the personal list.
   */
  private static buildResolveRequestBody(
    authorizerName: string,
    userIdNum: number,
    context: AuthorizerResolveContext
  ): AuthorizerRequestBody {
    const passportId = Number.parseInt(String(context.passportId ?? ''), 10);

    if (Number.isNaN(passportId) || passportId <= 0) {
      return { authorizerName, userId: userIdNum };
    }

    if (context.roleId === GROWER_ROLE_ID) {
      return { authorizerName, passportId, roleId: GROWER_ROLE_ID };
    }

    return { authorizerName, passportId };
  }

  private static extractAuthorizerId(response: unknown): number | undefined {
    if (typeof response !== 'object' || response === null) {
      return undefined;
    }

    const candidate: { authorizerId?: unknown; id?: unknown; authorizerID?: unknown } = response;
    for (const value of [candidate.authorizerId, candidate.id, candidate.authorizerID]) {
      if (typeof value === 'number' && value > 0) {
        return value;
      }
    }

    return undefined;
  }

  private getCurrentUserIdNumber(): number | undefined {
    const userId = localStorage.getItem('userID');
    if (!userId) {
      return undefined;
    }

    const parsed = Number.parseInt(userId, 10);
    return Number.isNaN(parsed) || parsed <= 0 ? undefined : parsed;
  }

  private buildAuthHeaders(): Record<string, string> | null {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('⚠️ No authentication token found - cannot call the authorizers API');
      return null;
    }

    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    return {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Syncs all pending authorizers and updates the authorisedByMap with real IDs
   * Returns true if all pending authorizers were synced successfully
   */
  async syncPendingAuthorizers(): Promise<boolean> {
    const syncContext = this.prepareSyncContext();
    if (!syncContext) {
      return false;
    }

    const { userIdNum, headers } = syncContext;
    const cachedAuthorizers = await this.offlineStorage.getAuthorisedUsers(userIdNum) || [];
    const pendingAuthorizers = cachedAuthorizers.filter((auth: any) => 
      auth.isPendingSync === true || (auth.authorizerId && auth.authorizerId < 0)
    );

    if (pendingAuthorizers.length === 0) {
      console.log('✅ No pending authorizers to sync');
      return true;
    }

    console.log(`🔄 Syncing ${pendingAuthorizers.length} pending authorizer(s)...`);

    const syncedNames: string[] = [];
    let allSynced = true;

    for (const auth of pendingAuthorizers) {
      const success = await this.syncSingleAuthorizer(auth, userIdNum, cachedAuthorizers, headers, syncedNames);
      if (!success) {
        allSynced = false;
      }
    }

    await this.offlineStorage.saveAuthorisedUsers(userIdNum, cachedAuthorizers);
  
    if (syncedNames.length > 0) {
      console.log(`✅ Successfully synced ${syncedNames.length} authorizer(s):`, syncedNames);
    }
    
    return allSynced;
  }

  /**
   * Validates prerequisites for syncing and returns auth context
   * Returns null if prerequisites are not met
   */
  private prepareSyncContext(): { userIdNum: number; headers: Record<string, string> } | null {
    if (!navigator.onLine) {
      console.log('📴 Device is offline - cannot sync authorizers');
      return null;
    }

    const userId = localStorage.getItem('userID');
    if (!userId) {
      console.log('⚠️ No user ID found - cannot sync authorizers');
      return null;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('⚠️ No authentication token found - cannot sync authorizers');
      return null;
    }

    const userIdNum = Number.parseInt(String(userId), 10);
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };

    return { userIdNum, headers };
  }

  /**
   * Syncs a single authorizer to the backend
   */
  private async syncSingleAuthorizer(
    auth: any,
    userIdNum: number,
    cachedAuthorizers: any[],
    headers: Record<string, string>,
    syncedNames: string[]
  ): Promise<boolean> {
    const authorizerName = auth.authorizerName || auth.name;
    const apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers/add`;
    
    try {
      const body = this.buildAuthorizerRequestBody(auth, authorizerName, userIdNum);
      const response = await firstValueFrom(this.http.post<any>(apiUrl, body, { headers, withCredentials: true }));
      
      return this.updateCachedAuthorizer(response, authorizerName, cachedAuthorizers, syncedNames);
    } catch (error) {
      console.warn(`  ❌ Failed to sync authorizer "${authorizerName}":`, error);
      return false;
    }
  }

  /**
   * Builds the request body based on authorizer type (Transloader vs Grower)
   */
  private buildAuthorizerRequestBody(auth: any, authorizerName: string, userIdNum: number): AuthorizerRequestBody {
    if (auth.passportId && auth.passportId > 0) {
      const body: any = { authorizerName, passportId: auth.passportId };
      if (auth.roleId) {
        body.roleId = auth.roleId;
        console.log(`  📤 Syncing Grower account-level authorizer: "${authorizerName}" (passportId: ${auth.passportId}, roleId: ${auth.roleId})`);
      } else {
        console.log(`  📤 Syncing Transloader authorizer: "${authorizerName}" (passportId: ${auth.passportId})`);
      }
      return body;
    }
    
    console.log(`  📤 Syncing Grower authorizer: "${authorizerName}" (userId: ${userIdNum})`);
    return { authorizerName, userId: userIdNum };
  }

  /**
   * Updates the cached authorizer with the real ID from the backend response
   */
  private updateCachedAuthorizer(response: any, authorizerName: string, cachedAuthorizers: any[], syncedNames: string[]): boolean {
    const realId = AuthorizerSyncService.extractAuthorizerId(response);
    const index = cachedAuthorizers.findIndex((a: any) => (a.authorizerName || a.name) === authorizerName);
    
    if (index < 0 || !realId) {
      console.warn(`  ⚠️ Could not find authorizer in cache or no ID in response:`, response);
      return false;
    }

    cachedAuthorizers[index].authorizerId = realId;
    cachedAuthorizers[index].isPendingSync = false;
    cachedAuthorizers[index].addedOffline = false;
    
    if (this.authorisedByMap) {
      this.authorisedByMap.set(authorizerName, realId);
    }
    syncedNames.push(authorizerName);
    
    console.log(`  ✅ Synced "${authorizerName}" → ID: ${realId}`);
    return true;
  }
}

